import React from "react";
import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface TalentfulCTAProps {
  totalRecruitersNeeded: number;
  ftePercentage: number;
  outsourcedPercentage: number;
  currentRecruiters: number;
  rampPeriodMonths: number;
  timelineMonths: number;
  monthlyHiringGoals: {
    tech: number;
    gtm: number;
    gna: number;
    exec: number;
  };
}

export const TalentfulCTA = ({
  totalRecruitersNeeded,
  ftePercentage,
  outsourcedPercentage,
  currentRecruiters,
  rampPeriodMonths,
  timelineMonths,
  monthlyHiringGoals,
}: TalentfulCTAProps) => {
  // Calculate final team size (current + additional needed)
  const finalTeamSize = currentRecruiters + totalRecruitersNeeded;
  
  // Calculate current FTE percentage
  const currentFtePercentage = (currentRecruiters / finalTeamSize) * 100;
  
  // Calculate target numbers based on percentages
  const targetFteCount = Math.round((finalTeamSize * ftePercentage) / 100);
  
  let additionalFteNeeded = 0;
  let talentfulRecruitersNeeded = 0;

  console.log("Team size calculation:", {
    currentRecruiters,
    totalRecruitersNeeded,
    finalTeamSize,
    currentFtePercentage,
    targetFtePercentage: ftePercentage,
    targetFteCount
  });

  // If current FTE percentage is higher than target
  if (currentFtePercentage > ftePercentage) {
    // All additional recruiters go to Talentful
    additionalFteNeeded = 0;
    talentfulRecruitersNeeded = totalRecruitersNeeded;
    
    console.log("Current FTE % higher than target - all additional to Talentful:", {
      additionalFteNeeded,
      talentfulRecruitersNeeded
    });
  } else {
    // Calculate how many more FTE needed to reach target percentage
    const fteNeededForTarget = targetFteCount - currentRecruiters;
    
    if (fteNeededForTarget > 0) {
      // Allocate recruiters to reach target split
      additionalFteNeeded = Math.min(fteNeededForTarget, totalRecruitersNeeded);
      talentfulRecruitersNeeded = totalRecruitersNeeded - additionalFteNeeded;
    } else {
      // If we already have enough FTE, all additional go to Talentful
      additionalFteNeeded = 0;
      talentfulRecruitersNeeded = totalRecruitersNeeded;
    }
    
    console.log("Calculated split based on target %:", {
      fteNeededForTarget,
      additionalFteNeeded,
      talentfulRecruitersNeeded
    });
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200 p-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-gray-900">Final Team Size</h3>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-4 w-4 text-gray-500 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[300px]">
                    <p>Includes current team members</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <p className={`text-3xl font-bold ${finalTeamSize > 0 ? 'text-green-500' : 'text-gray-900'}`}>{finalTeamSize}</p>
            <p className="text-sm text-gray-500">Total recruiting team members needed</p>
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200 p-6">
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-gray-900">Additional Recruiters</h3>
            <p className={`text-3xl font-bold ${totalRecruitersNeeded > 0 ? 'text-green-500' : 'text-gray-900'}`}>{totalRecruitersNeeded}</p>
            <p className="text-sm text-gray-500">New team members required</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200 p-6">
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-gray-900">FTE Recruiters</h3>
            <p className={`text-3xl font-bold ${additionalFteNeeded > 0 ? 'text-green-500' : 'text-gray-900'}`}>{additionalFteNeeded}</p>
            <p className="text-sm text-gray-500">Full-time employees to hire</p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200 p-6">
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-gray-900">Talentful Recruiters (Outsourced)</h3>
            <p className={`text-3xl font-bold ${talentfulRecruitersNeeded > 0 ? 'text-green-500' : 'text-gray-900'}`}>{talentfulRecruitersNeeded}</p>
            <p className="text-sm text-gray-500">Recommended Talentful team size</p>
          </div>
        </div>
      </div>
    </div>
  );
};